/**
 * Color filter dropdown for product variants
 * Manages color selection, search filtering, and grid row visibility
 */

import { reinitThumbnailScrolling } from './thumbnailScroll.js';
import { isSingleColorModeActive, applySingleColorMode } from './singleColorMode.js';
import { pinColorToTop } from './colorPin.js';
import { ensureProductLoaded, isProductLoaded } from './lazyProductLoader.js';
import { isMobileViewport } from '../utils/viewport.js';

/**
 * Apply color filter to warehouse rows based on current checkbox state
 * Updates display text, filtering classes, and triggers image resize
 */
function applyColorFilter(): void {
  const checkedCount = $("#colorDropdown_options input:checked").length;
  const totalCount = $("#colorDropdown_options input").length;

  // Update display text
  if (checkedCount === 0) {
    $(".selected_colors").html(
      '<span class="cf-dropdown__label-prefix">Colors:</span>All Colors'
    );
    $("#resetGrid").addClass("is-disabled");
  } else if (checkedCount === 1) {
    // Show color name when only one selected
    const colorName = $("#colorDropdown_options input:checked")
      .closest("li")
      .find(".cf-dropdown__option-text")
      .text()
      .trim();
    $(".selected_colors").html(colorName);
    $("#resetGrid").removeClass("is-disabled");
  } else {
    $(".selected_colors").html(checkedCount + " colors");
    $("#resetGrid").removeClass("is-disabled");
  }

  // Update "Select All" checkbox state
  if (checkedCount === totalCount) {
    $("#selectAll input[type='checkbox']").prop("checked", true);
  } else {
    $("#selectAll input[type='checkbox']").prop("checked", false);
  }

  // Apply filtering
  if (checkedCount < 1) {
    // No colors selected - show all rows
    $(".inventory_master_table").removeClass("filtering-active");
    $(".warehouse_row").removeClass("show-this-color single_related");
  } else {
    // Colors selected - activate filtering
    $(".inventory_master_table").addClass("filtering-active");
    $(".warehouse_row").removeClass("show-this-color single_related");

    if (checkedCount === 1) {
      $(".warehouse_row:first-child").addClass("single_related");
    }

    // Show only selected color rows
    $("#colorDropdown_options input:checkbox:checked").each(function (): false | void {
      const productHandle = $(this)
        .closest("li.cf-dropdown__item")
        .attr("data-product_handle");
      
      if (productHandle) {
        $(`.warehouse_row[data-product-handle="${productHandle}"]`).addClass("show-this-color");
      }
    });

    // Trigger image resize after filter is applied
    setTimeout(resizeProductImages, 100);
  }
}



/**
 * Map checked dropdown checkboxes to swatch active-color classes
 * Syncs dropdown state with product swatches on the page
 */
function mapSelectedDropdown(): void {
  $('#colorDropdown_options input[type="checkbox"]').each(function (): false | void {
    const isChecked = $(this).is(":checked");
    const productHandle = $(this).closest("li").attr("data-product_handle");

    if (!productHandle) {
      return;
    }

    // Find matching swatch and toggle active-color class
    $(".swatch-option").each(function (): false | void {
      if ($(this).attr("data-product_handle") === productHandle) {
        if (isChecked) {
          $(this).addClass("active-color");
        } else {
          $(this).removeClass("active-color");
        }
        return false;
      }
    });
  });
}

/**
 * Resize product images based on visible warehouse count
 * Accounts for both color and availability filtering
 */
function resizeProductImages(): void {
  $(".product_image_cell").each(function (): void {
    const $cell = $(this);
    const $img = $cell.find("img");
    const warehouseCount = parseInt($cell.attr("data-warehouse-count") ?? "1", 10) || 1;

    // Calculate visible warehouse rows for this product
    const $row = $cell.closest(".warehouse_row");
    if ($row.length) {
      const productHandle = $row.attr("data-product-handle");
      if (!productHandle) {
        return;
      }

      const $allProductRows = $(`.warehouse_row[data-product-handle="${productHandle}"]`);

      // Count visible rows based on filtering classes
      const $table = $(".inventory_master_table");
      const isColorFiltering = $table.hasClass("filtering-active");
      const isAvailabilityFiltering = $table.hasClass("availability-filtering");

      let visibleCount = 0;

      if (!isColorFiltering && !isAvailabilityFiltering) {
        // No filters - all rows visible
        visibleCount = $allProductRows.length;
      } else if (isColorFiltering && !isAvailabilityFiltering) {
        // Only color filter
        visibleCount = $allProductRows.filter(".show-this-color").length;
      } else if (!isColorFiltering && isAvailabilityFiltering) {
        // Only availability filter
        visibleCount = $allProductRows.filter(".show-this-warehouse").length;
      } else {
        // Both filters
        visibleCount = $allProductRows.filter(".show-this-color.show-this-warehouse").length;
      }

      const activeCount = visibleCount > 0 ? visibleCount : warehouseCount;

      // Set max-height: 50px per warehouse
      const maxHeight = 50 * activeCount;
      $img.css({
        "max-height": maxHeight + "px",
        width: "auto",
        height: "auto",
      });
    }
  });
}

/**
 * Reset color filter to show all colors
 * Clears all checkboxes and filtering classes
 */
function resetColorFilter(): void {
  // Remove color filtering
  $(".inventory_master_table").removeClass("filtering-active");
  $(".warehouse_row").removeClass("show-this-color");

  // Uncheck all checkboxes
  $("#colorDropdown input[type='checkbox']").prop("checked", false);
  
  // Reset display
  $(".selected_colors").html(
    '<span class="cf-dropdown__label-prefix">Colors:</span>All Colors'
  );
  $(".swatch-option").removeClass("active-color");
  $("#resetGrid").addClass("is-disabled");
}

/**
 * Height of the fixed page header plus the sticky pricing-table header — the
 * screen line above which grid content is hidden behind chrome.
 */
function measureStickyOffset(): number {
  const headerVar = getComputedStyle(document.documentElement)
    .getPropertyValue('--theme-sticky-header-height')
    .trim();
  const headerHeight = parseInt(headerVar, 10) || 0;

  const tableHeader = document.querySelector<HTMLElement>('.inventory_header_row');
  const tableHeaderHeight = tableHeader
    ? tableHeader.getBoundingClientRect().height
    : 0;

  return Math.ceil(headerHeight + tableHeaderHeight + 8);
}

/**
 * True when the user has scrolled down past the table header into the rows.
 *
 * Decides whether pinning a color should scroll. The swatch grid sits above the
 * pricing table (main-product.liquid:351 vs :619) and doesn't stick, so a user
 * picking a swatch is by definition near the top: pin silently and let them
 * find the color already at row 1 when they scroll down. A user deep in the
 * grid can only change color via the dropdown in the sticky table header, and
 * there the pinned row must be brought to them or the click looks like it did
 * nothing.
 *
 * Must be called BEFORE pinning — the hoist changes the layout it measures.
 */
function isScrolledIntoRows(): boolean {
  const tbody = document.querySelector<HTMLElement>('.inventory_table_tbody');
  if (!tbody) return false;
  return tbody.getBoundingClientRect().top < measureStickyOffset();
}

/**
 * Pin a color to row 1 in "Show Every Color" mode, scrolling to it only if the
 * user is already looking at the rows.
 */
function pinAndMaybeScroll(handle: string): void {
  const shouldScroll = isScrolledIntoRows();
  pinColorToTop(handle);
  if (shouldScroll) {
    window.setTimeout(() => scrollToProductHandle(handle), 0);
  }
}

/**
 * Scroll the first warehouse_row for the given product handle into view.
 * Used by the swatch click handler when "Show Every Color" is active — all
 * colors stay visible, the clicked one is just scrolled to.
 */
function scrollToProductHandle(handle: string): void {
  if (isMobileViewport()) {
    // On mobile the active card always sits first in .mobile-inventory-list
    // (singleColorMode reorder), and the global Update Cart button sits above
    // the list. Scroll the Update Cart button to the top of the viewport so
    // the user lands on a consistent, top-of-page anchor regardless of where
    // they were scrolled before picking a color.
    const submit = document.querySelector<HTMLElement>(
      ".mobile_table_wrapper > .mobile-card-submit",
    );
    const card = document.querySelector<HTMLElement>(
      `.mobile-inventory-product[data-product-handle="${handle}"]`,
    );
    const fallback = document.querySelector<HTMLElement>(".mobile_table_wrapper");
    const target = submit ?? card ?? fallback;
    if (!target) return;

    const headerVar = getComputedStyle(document.documentElement)
      .getPropertyValue("--theme-sticky-header-height")
      .trim();
    const headerHeight = parseInt(headerVar, 10) || 0;
    target.style.scrollMarginTop = `${Math.ceil(headerHeight + 8)}px`;
    target.scrollIntoView({ block: "start", behavior: "smooth" });

    if (card) {
      card.classList.remove("scrollto-variant-highlight");
      void card.offsetWidth;
      card.classList.add("scrollto-variant-highlight");
      card.addEventListener(
        "animationend",
        () => card.classList.remove("scrollto-variant-highlight"),
        { once: true },
      );
    }
    return;
  }

  const row = document.querySelector<HTMLElement>(
    `.warehouse_row[data-product-handle="${handle}"]`,
  );
  if (!row) return;

  // Match the sticky-header offset logic used by scrollToVariantFromUrl so
  // the sticky table header and page header don't overlap the target row.
  row.style.scrollMarginTop = `${measureStickyOffset()}px`;
  row.scrollIntoView({ block: "start", behavior: "smooth" });

  // Brief highlight so the user can see which row they landed on.
  row.classList.remove("scrollto-variant-highlight");
  void row.offsetWidth; // force reflow so the animation retriggers
  row.classList.add("scrollto-variant-highlight");
  row.addEventListener(
    "animationend",
    () => row.classList.remove("scrollto-variant-highlight"),
    { once: true },
  );
}

/**
 * Initialize color filter functionality
 * Sets up dropdown toggle, search, checkbox handlers, and button events
 */
export function initColorFilter(): void {
  // Dropdown toggle
  $(".cf-dropdown__trigger").on("click", function (): void {
    const $menu = $("#colorDropdown");
    const $trigger = $(this);
    const isOpen = $menu.is(":visible");

    $menu.toggle();
    $trigger.attr("aria-expanded", String(!isOpen));

    // Auto-focus and reset when opening dropdown
    if (!isOpen) {
      $("#colorSearch").val("").trigger("focus");
      $("#colorDropdown_options > li").show();
      $(".checkbox--all-colors").show();
    }
  });

  // Real-time search filtering
  $("#colorSearch").on("input", function (): void {
    const searchTerm = ($(this).val() as string).toLowerCase().trim();

    // Hide "All Colors" checkbox when search has text
    if (searchTerm === "") {
      $(".checkbox--all-colors").show();
    } else {
      $(".checkbox--all-colors").hide();
    }

    // Filter color list items (case-insensitive contains)
    $("#colorDropdown_options > li").each(function (): void {
      const colorName = ($(this).attr("data-color_name") ?? "").toLowerCase();
      if (colorName.includes(searchTerm)) {
        $(this).show();
      } else {
        $(this).hide();
      }
    });
  });

  // Enter key: select all visible filtered colors
  $("#colorSearch").on("keydown", function (e: JQuery.KeyDownEvent): void {
    if (e.key === "Enter") {
      e.preventDefault();
      const searchTerm = ($(this).val() as string).trim();

      // Only select if text was entered
      if (searchTerm !== "") {
        // Check all currently visible checkboxes
        $('#colorDropdown_options > li:visible input[type="checkbox"]').each(function (): void {
          $(this).prop("checked", true);
        });

        // Trigger the first checkbox's click event to update the UI
        $("#colorDropdown input").first().trigger("click");

        // Close dropdown after selection
        $("#colorDropdown").hide();
        $(".cf-dropdown__trigger").attr("aria-expanded", "false");
      }
    } else if (e.key === "Escape") {
      // Escape key: close dropdown (maintains filters)
      e.preventDefault();
      $("#colorDropdown").hide();
      $(".cf-dropdown__trigger").attr("aria-expanded", "false");
    }
  });

  // Hide color dropdown when click outside
  const $targetDiv = $("#allColors");
  $(document).on("click", function (event: JQuery.ClickEvent): void {
    // Check if the click event target is not the target div or its descendants
    if (
      !$targetDiv.is(event.target) &&
      $targetDiv.has(event.target as Element).length === 0
    ) {
      $("#colorDropdown").hide();
      $(".cf-dropdown__trigger").attr("aria-expanded", "false");
    }
  });

  // Checkbox change handler — lazy-load any unloaded products before applying filter.
  // When single color mode is active, behaves identically to a swatch click:
  // the selected color is shown at the top plus any rows with values filled in.
  $("#colorDropdown input").on("click", function (): void {
    if (isSingleColorModeActive()) {
      const handle = $(this).closest("li.cf-dropdown__item").attr("data-product_handle");
      if (!handle) {
        // "Select All" / non-color checkbox — ignore in single color mode
        $(this).prop("checked", false);
        return;
      }

      // Enforce single-select: uncheck every other color checkbox
      $("#colorDropdown_options input:checkbox").not(this).prop("checked", false);
      $(this).prop("checked", true);

      // Mirror the swatch highlight so both UI controls stay in sync
      $(".Product_swatch_colors .swatch-option").removeClass("active-color");
      $(`.Product_swatch_colors .swatch-option[data-product_handle="${handle}"]`).addClass("active-color");

      // Load rows if needed, then apply single color mode (same as swatch click)
      void ensureProductLoaded(handle).then((): void => {
        if (!isProductLoaded(handle)) return;
        applySingleColorMode(handle);
        window.setTimeout(() => scrollToProductHandle(handle), 0);
        const colorName = $(
          `#colorDropdown_options li[data-product_handle="${handle}"] .cf-dropdown__option-text`
        ).text().trim();
        if (colorName) {
          $(".selected_colors").html(colorName);
        }
      });
      return;
    }

    // Normal mode: lazy-load all checked products then apply color filter
    const checkedHandles: string[] = [];
    $("#colorDropdown_options input:checkbox:checked").each(function (): void {
      const handle = $(this).closest("li.cf-dropdown__item").attr("data-product_handle");
      if (handle) {
        checkedHandles.push(handle);
      }
    });

    // Pin the color the user just checked. Only on check — unchecking a box
    // removes a color from the filter and says nothing about which one should
    // lead. This is the one control reachable from deep in the grid (it lives
    // in the sticky table header), so it's the path that usually scrolls.
    const $clicked = $(this);
    const justChecked = $clicked.is(":checked")
      ? $clicked.closest("li.cf-dropdown__item").attr("data-product_handle")
      : undefined;

    if (checkedHandles.length === 0) {
      applyColorFilter();
      return;
    }

    void Promise.all(checkedHandles.map((h) => ensureProductLoaded(h))).then((): void => {
      applyColorFilter();
      if (justChecked && isProductLoaded(justChecked)) {
        pinAndMaybeScroll(justChecked);
      }
    });
  });

  // Manual checkbox click handler (with delayed map sync)
  $('#colorDropdown_options input[type="checkbox"]').on("click", function (event: JQuery.ClickEvent): void {
    // Check if event is manually triggered by user (not programmatic)
    // Use type assertion since isTrigger is a jQuery property not in typedefs
    if (!(event as any).isTrigger) {
      setTimeout(function (): void {
        mapSelectedDropdown();
      }, 2000);
    }
  });

  // Clear All button
  $("#clearAll").on("click", function (): void {
    resetColorFilter();
  });

  // Reset Grid button
  $("#resetGrid").on("click", function (): void {
    resetColorFilter();
  });

  // Select All button - clears all selections and shows all products
  $("#selectAll").on("click", function (e: JQuery.ClickEvent): void {
    e.preventDefault();
    resetColorFilter();

    // Close the dropdown
    $("#colorDropdown").hide();
    $(".cf-dropdown__trigger").attr("aria-expanded", "false");
  });

  // Initialize selected color display on page load
  $(document).ready(function (): void {
    const swatchColorName = $(".product-detail__title").attr("data-color");
    if (swatchColorName) {
      $(".selected_color_swatch").html(swatchColorName);
    }
    $(".Product_swatch_colors .swatch-option").first().addClass("active-color");

    // Initial image resize after page load
    setTimeout(resizeProductImages, 500);
  });

  // Mobile native <select> color picker. On change, behave like a swatch click
  // in single-color mode (which is locked on for mobile): lazy-load the chosen
  // color's rows, apply single-color mode (which prepends the active card and
  // sorts the rest alphabetically), and scroll the user to the new card.
  $(document).on("change", "#mobileColorSelect", function (): void {
    const handle = ($(this).val() as string) || "";
    if (!handle) return;

    // Mirror the desktop swatch active-color highlight so the rest of the
    // page stays in sync (color overlay text, etc.).
    $(".Product_swatch_colors .swatch-option").removeClass("active-color");
    $(`.Product_swatch_colors .swatch-option[data-product_handle="${handle}"]`).addClass("active-color");

    void ensureProductLoaded(handle).then((): void => {
      if (!isProductLoaded(handle)) return;
      applySingleColorMode(handle);
      window.setTimeout(() => scrollToProductHandle(handle), 0);
    });
  });

  // Product swatch click handler
  $(document).on("click", ".Product_swatch_colors .swatch-option", function (): void {
    // COMING SOON: a not-released colour has no row in the pricing grid by
    // construction (main-product.liquid builds `grid_family` without it), so
    // the filter path below would call ensureProductLoaded() for a row that
    // never renders and then no-op — a dead click on a visible swatch.
    // Navigate to the colour's own page instead, which is where its Coming
    // Soon badge and expected date live.
    const comingSoonUrl = $(this).attr("data-coming-soon-url");
    if ($(this).attr("data-coming-soon") === "true" && comingSoonUrl) {
      window.location.href = comingSoonUrl;
      return;
    }

    const productHandleSelected = $(this).attr("data-product_handle");
    const swatchColorName = $(this).find(".swatch-option_name").attr("data-color");

    if (swatchColorName) {
      $(".selected_color_swatch").html(swatchColorName);
    }

    // ALWAYS remove active-color from ALL swatches first (prevents multiple selections)
    $(".Product_swatch_colors .swatch-option").removeClass("active-color");

    // Add active-color to the clicked swatch
    $(this).addClass("active-color");

    if (isSingleColorModeActive()) {
      // Single color mode: ensure product rows are loaded first, then filter
      if (productHandleSelected) {
        void ensureProductLoaded(productHandleSelected).then(() => {
          if (!isProductLoaded(productHandleSelected)) return;
          applySingleColorMode(productHandleSelected);
          // Auto-scroll-to-color on swatch click intentionally disabled — a
          // swatch click should update the product image without jumping the
          // user down to the pricing grid. Kept commented for easy reintroduction.
          // window.setTimeout(
          //   () => scrollToProductHandle(productHandleSelected),
          //   0,
          // );
          // Sync the dropdown label so it shows the currently selected color
          const colorName = $(
            `#colorDropdown_options li[data-product_handle="${productHandleSelected}"] .cf-dropdown__option-text`
          ).text().trim();
          if (colorName) {
            $(".selected_colors").html(colorName);
          }
        });
      }
    } else if (productHandleSelected) {
      // "Show Every Color" mode: every color stays visible and unfiltered —
      // the clicked one is just hoisted to row 1. Rows may not be in the DOM
      // yet on a lazy-loaded family, so load before pinning.
      void ensureProductLoaded(productHandleSelected).then((): void => {
        if (!isProductLoaded(productHandleSelected)) return;
        pinAndMaybeScroll(productHandleSelected);
      });
    }

    // Swap the product images for the selected color via the Section Rendering
    // API. The server-rendered HTML (sections/product-images-lazy.liquid → the
    // shared snippets/product-images.liquid) is identical to the initial page
    // render, so the swapped image keeps the correct size cap, responsive/lazy
    // loading, and the gallery/zoom data hooks. This replaces the old hand-built
    // markup from /products/{handle}.json, which dropped the `.shukchuk` wrapper
    // and made the image grow on every swatch click.
    if (!productHandleSelected) {
      return;
    }

    void swapProductImages(productHandleSelected, swatchColorName);
  });

  // Thumbnail click handler
  $(document).on("click", ".product-detail__thumbnail", function (e: JQuery.ClickEvent): void {
    e.preventDefault();

    // Remove active class from all thumbnails
    $(".product-detail__thumbnail").removeClass("thumb-active");

    // Add active class to clicked thumbnail
    $(this).addClass("thumb-active");

    // Get the index of clicked thumbnail
    const clickedIndex = $(".product-detail__thumbnail").index(this);

    // If Slick is initialized, navigate to that slide
    const $images = $(".product-detail__images");
    if ($images.hasClass("slick-initialized") && typeof ($images as any).slick === 'function') {
      ($images as any).slick("slickGoTo", clickedIndex);
    } else {
      // Fallback: Show/hide images directly if no Slick
      $images.find(".product-detail__image").hide();
      $images.find(".product-detail__image").eq(clickedIndex).show();
    }
  });
}

// Section Rendering API section name for the product image gallery.
// Must match the filename of sections/product-images-lazy.liquid.
const IMAGES_SECTION = "product-images-lazy";

/**
 * Swap the main product image gallery + thumbnails to the selected color by
 * fetching server-rendered HTML via the Shopify Section Rendering API.
 *
 * Rendering server-side (rather than hand-building markup from
 * /products/{handle}.json) guarantees the swapped image is structurally
 * identical to the initial page render — same `.rimage-outer-wrapper.shukchuk`
 * size cap, lazysizes responsive loading, and data-* gallery/zoom hooks.
 *
 * @param handle - the sibling product handle to load images for
 * @param colorName - the selected color label, used for the title data attr
 */
async function swapProductImages(
  handle: string,
  colorName: string | null | undefined,
): Promise<void> {
  const $imagesContainer = $(".product-detail__images-container");
  const $existingImages = $(".product-detail__images");
  const $mediaAndThumbs = $(".product-layout-grid__images-and-thumbs");

  // Lock the container height during the swap to avoid a layout jump.
  const currentHeight = $existingImages.outerHeight();
  if (currentHeight) {
    $imagesContainer.css("min-height", `${currentHeight}px`);
  }
  $mediaAndThumbs.css("opacity", "0.5");

  function restoreLoadingState(): void {
    $mediaAndThumbs.css("opacity", "1");
    $imagesContainer.css("min-height", "");
  }

  let html: string | undefined;
  try {
    const response = await fetch(
      `/products/${encodeURIComponent(handle)}?sections=${IMAGES_SECTION}`,
    );
    if (!response.ok) {
      console.error(
        `[ColorSwatch] HTTP ${response.status} loading images for: ${handle}`,
      );
      restoreLoadingState();
      return;
    }
    const data = (await response.json()) as Record<string, string>;
    html = data[IMAGES_SECTION];
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[ColorSwatch] Failed to load images for ${handle}: ${message}`);
    restoreLoadingState();
    return;
  }

  if (!html) {
    console.error(
      `[ColorSwatch] No HTML for section "${IMAGES_SECTION}" (handle: ${handle})`,
    );
    restoreLoadingState();
    return;
  }

  // The Section Rendering API wraps output in <div class="shopify-section">…</div>.
  // The content here is <div>-based (not <tr>), so it parses correctly inside a
  // plain container and querySelector reaches through the wrapper.
  const temp = document.createElement("div");
  temp.innerHTML = html;

  const newImages = temp.querySelector<HTMLElement>(".product-detail__images");
  const newThumbnails = Array.from(
    temp.querySelectorAll<HTMLElement>(".product-detail__thumbnail"),
  );

  if (!newImages) {
    console.error(
      `[ColorSwatch] No .product-detail__images in section response for: ${handle}`,
    );
    restoreLoadingState();
    return;
  }

  // Tear down the existing slick instance before swapping out its DOM. Re-query
  // rather than reusing the pre-fetch capture: the theme re-runs its own
  // assessMediaSlideshow on `debouncedresize`, so the live instance may not be
  // the one that was there when the fetch started.
  const $slickImages = $imagesContainer.children(".product-detail__images");
  if (
    $slickImages.hasClass("slick-initialized") &&
    typeof ($slickImages as any).slick === "function"
  ) {
    ($slickImages as any).slick("unslick");
  }

  // Replace the main image gallery in place. Swapping over the existing node
  // keeps the gallery in the same DOM position the server render put it —
  // after `.product-current-color-overlay`, which is statically positioned
  // (and therefore order-sensitive) at the mobile breakpoint. Falls back to
  // inserting after the overlay for media-less products, where the server
  // render emits no `.product-detail__images` to replace.
  const $currentImages = $imagesContainer.children(".product-detail__images");
  if ($currentImages.length > 0) {
    $currentImages.replaceWith(newImages);
  } else {
    const $overlay = $imagesContainer.children(".product-current-color-overlay");
    if ($overlay.length > 0) {
      $(newImages).insertAfter($overlay);
    } else {
      $imagesContainer.prepend(newImages);
    }
  }

  // Replace the thumbnail strip content, preserving the scroll arrows/wrapper.
  // Always reset the wrapper to the new product's thumbnails — emptying it when
  // the new color has none (e.g. a single-image sibling) so stale thumbnails from
  // the previous color don't linger.
  const $thumbnailsWrapper = $(".product-detail__thumbnails-scroll-wrapper");
  if ($thumbnailsWrapper.length > 0) {
    $thumbnailsWrapper.empty();
    if (newThumbnails.length > 0) {
      $thumbnailsWrapper.append(newThumbnails);
    }
  }

  // Update the product title color attribute.
  if (colorName) {
    $(".product-detail__title").attr("data-color", colorName);
  }

  $mediaAndThumbs.css("opacity", "1");

  // Clear slick control containers to prevent duplicate arrows/dots.
  $(".slick-arrows, .slick-dots").empty();

  // Hand the swapped-in gallery back to the theme's own product section so it
  // re-initializes exactly as it does on first page load.
  resyncThemeProductGallery();

  // Nudge lazysizes to load the freshly injected images.
  const lazySizes = (window as any).lazySizes;
  if (lazySizes?.loader?.checkElems) {
    lazySizes.loader.checkElems();
  }

  // Release the height lock once the new images have settled.
  window.setTimeout(() => {
    $imagesContainer.css("min-height", "");
  }, 100);

  // Reinitialize thumbnail scroll arrows for the new strip.
  window.setTimeout(() => {
    void $(".product-detail__thumbnails")[0]?.offsetHeight; // force reflow
    reinitThumbnailScrolling();
  }, 300);
}

/**
 * Hand the freshly swapped-in gallery back to the theme's own product section.
 *
 * theme.Product caches jQuery references to the gallery nodes once, at section
 * load (theme.js `onSectionLoad`), and drives everything from them:
 *   - `assessMediaSlideshow` slick-initializes `$productMediaContainer`
 *   - `_updatePrimaryProductMediaFromThumbnail` calls `slickGoTo` on it
 *   - a window `debouncedresize` handler is bound to the same object
 *
 * Replacing the gallery DOM leaves every one of those pointing at the detached
 * old node. That is why a swapped color rendered all of its images stacked (the
 * theme never re-initialized the slideshow on the new node) and why its
 * thumbnails stopped driving the main image.
 *
 * Re-pointing the references and letting the theme run its own init means the
 * swapped gallery gets byte-identical behavior to the initial page render —
 * fade transition, external arrows, `thumb-active` sync, tab-order fix — rather
 * than a hand-rolled option set that silently drifts from the theme's.
 *
 * No visual resize comes from this: the image box is pinned by CSS on the
 * server-rendered `.rimage-outer-wrapper.shukchuk` wrapper (max 450px, see
 * styles.css), and the theme's one size-affecting slick option
 * (`adaptiveHeight`) is already neutralized by the
 * `.product-detail__images .slick-list.draggable { height: auto !important }`
 * override in Custom-LEIS.css.
 */
function resyncThemeProductGallery(): void {
  const themeProduct = window.theme?.Product;
  const assessMediaSlideshow = themeProduct?.functions?.assessMediaSlideshow;
  const $container = themeProduct?.$container;

  if (
    !themeProduct ||
    typeof assessMediaSlideshow !== "function" ||
    !$container ||
    $container.length === 0
  ) {
    console.warn(
      "[ColorSwatch] theme.Product unavailable; falling back to local slideshow init",
    );
    initImageSlideshow();
    return;
  }

  themeProduct.$productMediaContainer = $(".product-detail__images", $container);
  themeProduct.$productMedia = $("[data-product-media]", $container);
  themeProduct.$productImages = $("[data-product-image]", $container);
  themeProduct.$productMediaThumbnails = $(
    "[data-product-media-thumbnail]",
    $container,
  );

  // assessMediaSlideshow no-ops while it believes a slideshow is already up.
  // The one it was tracking went away with the old node.
  themeProduct.mediaSlideshowActive = false;

  try {
    assessMediaSlideshow.call(themeProduct);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.warn(`[ColorSwatch] theme slideshow re-init failed: ${message}`);
    initImageSlideshow();
  }
}

/**
 * Fallback slideshow init, used only when theme.Product isn't reachable.
 *
 * Options mirror theme.js `assessMediaSlideshow` so the fallback cannot render
 * the gallery differently from the primary path — the previous hand-rolled set
 * (no `fade`, `arrows: true`, different arrow container) was what left every
 * image visible at once.
 */
function initImageSlideshow(): void {
  const $images = $(".product-detail__images");
  if ($images.length === 0 || typeof ($ as any).fn.slick !== "function") {
    console.warn("[ColorSwatch] slick unavailable; leaving images stacked");
    return;
  }
  try {
    ($images as any).slick({
      infinite: false,
      fade: true,
      arrows: false,
      dots: false,
      adaptiveHeight: true,
      appendArrows: $(".slick-external-controls .slick-arrows"),
      appendDots: $(".slick-external-controls .slick-dots"),
      initialSlide: 0,
      waitForAnimate: false,
      responsive: [
        {
          breakpoint: 767.98,
          settings: {
            fade: false,
            arrows: true,
            dots: $images.children().length > 1,
          },
        },
      ],
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.warn(`[ColorSwatch] slick init failed: ${message}`);
  }
}
